import React from 'react';

import {
  ActionIcon,
  Button,
  Center,
  Checkbox,
  Group,
  LoadingOverlay,
  NumberInput,
  Radio,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconPlus, IconTags } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import HelpButton from 'components/HelpButton';
import MoneyField from 'components/MoneyField';
import { useAuth } from 'context/auth-context';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import useCreatePayRate from 'hooks/payRate/useCreatePayRate';
import usePayRate from 'hooks/payRate/usePayRate';
import useUpdatePayRate from 'hooks/payRate/useUpdatePayRate';
import { PayRate, PayRateTier } from 'types/generated';
import { getChangedKeysForPayRateEdit } from 'utils/analytics-selectors';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import IncludeAttendanceOptions from './IncludeAttendanceOptions';
import PayRateTierRow from './PayRateTierRow';

export interface PayRateFormValues {
  payRateId: string;
  name: string;
  rate: number | null;
  percentage: number | null;
  payRateType: PayRate['payRateType'];
  additionalPerAttendee: boolean;
  payForNoShow: boolean;
  payForLateCancel: boolean;
  payRateTiers: Array<PayRateTier>;
}

interface IDrawerPayRateFormProps {
  formId: string;
  title: JSX.Element;
  opened: boolean;
  closeHandler: () => void;
  payRateId?: string;
  type: 'create' | 'edit';
  permission?: boolean;
}

const validateName = (name: string): string | null =>
  name.length < 3 || name.length > 120 ? 'Name must be between 3-120 characters' : null;

const validateRate = (values: PayRateFormValues): string | null => {
  if (values.payRateType === 'PERCENTAGE') return null;
  return Number(values.rate) >= 0 ? null : 'Payout must be provided';
};

const validatePercentage = (values: PayRateFormValues): string | null => {
  if (values.payRateType !== 'PERCENTAGE') return null;
  return Number(values.percentage) >= 0 && Number(values.percentage) <= 100
    ? null
    : 'Percentage must be between 0 and 100';
};

const validatePayRateTiers = (values: PayRateFormValues): string | null =>
  values.additionalPerAttendee && values.payRateTiers.length === 0
    ? 'Add at least one payment tier for additional per attendee'
    : null;

const hasAttendanceOptions = (values: PayRateFormValues): boolean =>
  values.payRateType === 'PERCENTAGE' || values.additionalPerAttendee;

const toTrackingPayRateType = (payRateType: PayRate['payRateType']): 'base' | 'hourly' | 'percentage' =>
  payRateType.toLowerCase() as 'base' | 'hourly' | 'percentage';

const DrawerPayRateForm = ({
  opened,
  closeHandler,
  formId,
  title,
  payRateId,
  type,
  permission = true,
}: IDrawerPayRateFormProps): JSX.Element => {
  const { session } = useAuth();
  const { trackPayRateCreated, trackPayRateUpdated } = useStaffTracking();

  const createMutation = useCreatePayRate();
  const updateMutation = useUpdatePayRate();
  const { data, isLoading, isFetching, isSuccess } = usePayRate(payRateId);

  const form = useForm<PayRateFormValues>({
    initialValues: {
      payRateId: '',
      name: '',
      rate: 0,
      percentage: null,
      payRateType: 'BASE',
      additionalPerAttendee: false,
      payForNoShow: false,
      payForLateCancel: false,
      payRateTiers: [],
    },
    validate: values => ({
      name: validateName(values.name),
      rate: validateRate(values),
      percentage: validatePercentage(values),
      payRateTiers: validatePayRateTiers(values),
    }),

    transformValues: values => ({
      ...values,
      payRateId: type === 'create' ? uuidv4() : values.payRateId,
      rate: values.payRateType === 'PERCENTAGE' ? null : Math.round(values.rate! * 100),
      percentage: values.payRateType === 'PERCENTAGE' ? Number((values.percentage! / 100).toFixed(4)) : null,
      payForNoShow: hasAttendanceOptions(values) && values.payForNoShow,
      payForLateCancel: hasAttendanceOptions(values) && values.payForLateCancel,
      payRateTiers: values.additionalPerAttendee
        ? values.payRateTiers.map(tier => ({
            ...tier,
            amount: Math.round(tier.amount * 100),
          }))
        : [],
    }),
  });

  const errorMessages = Object.entries(form.errors).map(error => error[1]);

  const handleAdditionalPerAtendee = (e: React.ChangeEvent<HTMLInputElement>): void => {
    form.setFieldValue('additionalPerAttendee', e.currentTarget.checked);
    if (e.currentTarget.checked && !form.values.payRateTiers.length) {
      form.insertListItem('payRateTiers', {
        minAttendants: 1,
        maxAttendants: null,
        amount: 0,
      });
    }
  };

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleAddPayRateTier = (): void => {
    const { payRateTiers } = form.values;
    if (payRateTiers.length) {
      const lastIndex = payRateTiers.length - 1;
      const { minAttendants } = payRateTiers[lastIndex];
      form.setFieldValue(`payRateTiers.${lastIndex}`, {
        ...payRateTiers[lastIndex],
        maxAttendants: minAttendants,
      });
      form.insertListItem('payRateTiers', {
        minAttendants: minAttendants + 1,
        maxAttendants: null,
        amount: 0,
      });
    } else {
      form.insertListItem('payRateTiers', {
        minAttendants: 1,
        maxAttendants: null,
        amount: 0,
      });
    }
  };

  const handleSubmit = (payload: PayRateFormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();
    if (type === 'edit') {
      updateMutation.mutate(payload, {
        onSuccess: response => {
          trackPayRateUpdated({
            pay_rate_id: payload.payRateId,
            pay_rate_type: toTrackingPayRateType(payload.payRateType),
            fields_changed: getChangedKeysForPayRateEdit(data?.data, payload),
            include_no_shows: payload.payForNoShow,
            include_late_cancels: payload.payForLateCancel,
          });
          successNotification('Pay rate update success', `${response.data.name} successfully updated.`);
          handleCloseAndReset();
        },
        onError: error => {
          errorNotification('Pay rate update failed', error.response?.data.message);
        },
      });
    } else {
      createMutation.mutate(payload, {
        onSuccess: response => {
          trackPayRateCreated({
            pay_rate_id: payload.payRateId,
            pay_rate_type: toTrackingPayRateType(payload.payRateType),
            percentage: payload.percentage,
            additional_per_attendee: payload.additionalPerAttendee,
            include_no_shows: payload.payForNoShow,
            include_late_cancels: payload.payForLateCancel,
          });
          successNotification('Pay rate creation success', `${response.data.name} successfully created.`);
          handleCloseAndReset();
        },
        onError: error => {
          errorNotification('Pay rate creation failed', error.response?.data.message);
        },
      });
    }
  };

  const handlePayRateTypeChange = (value: string): void => {
    const prev = form.values.payRateType;
    form.setFieldValue('payRateType', value as PayRate['payRateType']);
    if (value === 'PERCENTAGE') {
      form.setFieldValue('rate', null);
      form.setFieldValue('percentage', 0);
      form.setFieldValue('additionalPerAttendee', false);
      form.setFieldValue('payForNoShow', false);
      form.setFieldValue('payForLateCancel', false);
      form.setFieldValue('payRateTiers', []);
    } else if (prev === 'PERCENTAGE') {
      form.setFieldValue('rate', 0);
      form.setFieldValue('percentage', null);
    }
  };

  useShallowEffect(() => {
    if (type === 'edit' && data?.data && isSuccess) {
      form.setValues({
        ...data.data,
        rate: data.data.rate !== null ? data.data.rate / 100 : null,
        percentage: data.data.percentage !== null ? data.data.percentage * 100 : null,
        payRateTiers: data.data.payRateTiers.map(tier => ({
          ...tier,
          amount: tier.amount / 100,
        })),
      });
    }
  }, [data]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={3}>{title}</Title>}
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            disabled={!permission}
            data-cy='savePayRateButton'
            loading={createMutation.isLoading || updateMutation.isLoading || isLoading || isFetching}
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay visible={type === 'edit' && (isLoading || isFetching)} />
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            label='Name'
            {...form.getInputProps('name')}
            placeholder='Set Pay Rate Name'
            required
            icon={<IconTags />}
            data-cy='payRateNameTextInput'
            disabled={!permission}
          />
          <Radio.Group
            {...form.getInputProps('payRateType')}
            onChange={(value: string) => handlePayRateTypeChange(value)}
          >
            <Radio
              data-cy='payRateHourlyTypeButton'
              value='HOURLY'
              label='Hourly Pay Rate'
              disabled={!permission}
            />
            <Radio
              data-cy='payRateBaseTypeButton'
              value='BASE'
              label='Base Pay Rate'
              my='md'
              disabled={!permission}
            />
            <Group>
              <Radio
                data-cy='payRatePercentageTypeButton'
                value='PERCENTAGE'
                label='Percentage-Based Pay Rate'
                disabled={!permission}
              />
              <HelpButton width={240} position='right'>
                <Text size='sm'>
                  The percentage is applied to the sum of the session values of the athletes who checked in.
                  Turn on Include Late Cancels or Include No Shows to also count athletes whose credit was
                  deducted for a late cancel or no show.
                </Text>
              </HelpButton>
            </Group>
          </Radio.Group>
          {form.values.payRateType === 'PERCENTAGE' ? (
            <>
              <NumberInput
                label='Pay Rate Percentage*'
                placeholder='Enter Pay Rate percentage'
                data-cy='payRatePercentageNumberInput'
                required
                min={0}
                max={100}
                precision={2}
                icon={<Text size='sm'>%</Text>}
                disabled={!permission}
                {...form.getInputProps('percentage')}
              />
              <IncludeAttendanceOptions form={form} disabled={!permission} creditCondition indent={false} />
            </>
          ) : (
            <MoneyField
              country={session?.gym?.country}
              disabled={!permission}
              label='Pay Rate'
              placeholder='Enter pay rate'
              data-cy='payRateRateNumberInput'
              required
              {...form.getInputProps('rate')}
            />
          )}
          {form.values.payRateType !== 'PERCENTAGE' && (
            <>
              <Checkbox
                data-cy='additionalPerAttendeeButton'
                checked={form.values.additionalPerAttendee}
                disabled={!permission}
                {...form.getInputProps('additionalPerAttendee')}
                onChange={handleAdditionalPerAtendee}
                label='Add Per Head'
                style={{ paddingTop: 20 }}
              />
              {form.values.additionalPerAttendee && (
                <Stack>
                  {form.values.payRateTiers.map((_, index) => (
                    <PayRateTierRow key={index} index={index} form={form} disabled={!permission} />
                  ))}
                  <ActionIcon
                    variant='subtle'
                    color='kiloBlue'
                    onClick={handleAddPayRateTier}
                    disabled={!permission}
                    data-cy='addPayRateTierButton'
                  >
                    <IconPlus />
                  </ActionIcon>

                  <IncludeAttendanceOptions
                    form={form}
                    disabled={!permission}
                    creditCondition={false}
                    indent
                  />
                </Stack>
              )}
            </>
          )}
          {errorMessages.length > 0 && (
            <>
              <Stack py='sm' align='center'>
                {errorMessages.map(msg => (
                  <Center key={`msg-${msg}`}>
                    <Text size='xs' c='red'>
                      {msg}
                    </Text>
                  </Center>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </form>
    </Drawer>
  );
};

export default DrawerPayRateForm;
